import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { FINISHARRAY, STATUSARRAY } from "../../_contstants/common";
import { clearProductDetails } from "./productSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import FormAttachmentUpload from "../../components/formFields/FormAttachmentUpload";
import { MdImageNotSupported } from "react-icons/md";
import {
  createProduct,
  deleteProductImages,
  FindAllCategoryWiseSubCategoryList,
  getProductByID,
  ProductImageUpload,
  ProductMainImageUpload,
  updateProduct,
} from "./services/product.services";
import { createLookup, slugify } from "../../_helper/helper";
import { getAllCategory } from "../category/services/category.services";
import { getAllSubCategory } from "../subcategory/services/subCategory.services";
import { getAllSize } from "../size/services/size.services";

const CreateProduct: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ProductID = location.state?.ProductID;
  const ProductDetail = useAppSelector((state) => state.product.ProductDetail);
  const loading = useAppSelector((state) => state.product.loading);
  const [formKey, setFormKey] = React.useState(1);
  // const CategoryList = useAppSelector((state) => state.category.CategoryList);
  const CategoriesList = useAppSelector(
    (state) => state.product.CategoriesList,
  );
  const SizeList = useAppSelector((state) => state.size.SizeList);
  const [selectedCategory, setSelectedCategory] = useState<any>();
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>({});

  useEffect(() => {
    dispatch(FindAllCategoryWiseSubCategoryList());
    dispatch(getAllCategory());
    dispatch(getAllSubCategory());
    dispatch(getAllSize());
    if (ProductID) {
      dispatch(getProductByID(ProductID));
    }
    return () => {
      dispatch(clearProductDetails());
    };
  }, [ProductID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ProductDetail]);

  useEffect(() => {
    ProductDetail &&
      ProductDetail?.CategoryID &&
      setSelectedCategory(ProductDetail?.CategoryID);
  }, [ProductDetail]);

  useEffect(() => {
    if (CategoriesList && CategoriesList?.length > 0) {
      setSelectedSubCategory(createLookup(CategoriesList, "ID"));
    }
  }, [CategoriesList]);

  const handleGetSubCategory = (e: any) => {
    e && setSelectedCategory(e?.value);
  };

  const handleSubmit = async (values: any) => {
    const formData = {
      ProductName: values?.ProductName ? values.ProductName : "",
      ProductURL: values?.ProductURL ? values.ProductURL : "",
      CategoryID: values?.CategoryID ? values.CategoryID : null,
      SubCategoryID: values?.SubCategoryID ? values.SubCategoryID : null,
      ProductID: values?.ProductID ? values.ProductID : "",
      SizeID: values?.SizeID ? values.SizeID : null,
      Finish: values?.Finish ? values.Finish : "",
      Thickness: values?.Thickness ? values.Thickness : "",
      Description: values?.Description ? values.Description : "",
      MetaTitle: values?.MetaTitle ? values.MetaTitle : "",
      MetaDescription: values?.MetaDescription ? values.MetaDescription : "",
      MetaKeywords: values?.MetaKeywords ? values.MetaKeywords : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const mainImg = new FormData();
    const MainImage =
      values?.MainImage &&
      typeof values?.MainImage !== "string" &&
      Array.isArray(values?.MainImage) &&
      values?.MainImage?.length > 0
        ? values.MainImage[0].getRawFile()
        : "";
    try {
      if (ProductID) {
        const editPayload = {
          ...formData,
          ID: ProductID,
        };
        const response = await dispatch(updateProduct(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Images &&
              values.Images.length > 0 &&
              typeof values.Images !== "string") ||
            (values.MainImage &&
              typeof values.MainImage !== "string" &&
              ProductID)
          ) {
            const Images = values.Images?.map((fileItem: any) =>
              fileItem.getRawFile(),
            );

            Images?.map((image: any) => ImageData.append("Image", image));
            mainImg.append("MainImage", MainImage);
            mainImg.append("ID", ProductID);
            ImageData.append("MainProductID", ProductID);
            await dispatch(ProductImageUpload(ImageData));
            await dispatch(ProductMainImageUpload(mainImg));
          }
          navigate("/product");
        }
      } else {
        const response = await dispatch(createProduct(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Images &&
              values.Images.length > 0 &&
              typeof values.Images !== "string") ||
            (values.MainImage &&
              typeof values.MainImage !== "string" &&
              response?.payload?.Data?.ID)
          ) {
            const Images = values.Images?.map((fileItem: any) =>
              fileItem.getRawFile(),
            );
            Images?.map((image: any) => ImageData.append("Image", image));
            mainImg.append("MainImage", MainImage);
            mainImg.append("ID", response?.payload?.Data?.ID);
            ImageData.append("MainProductID", response?.payload?.Data?.ID);
            await dispatch(ProductImageUpload(ImageData));
            await dispatch(ProductMainImageUpload(mainImg));
          }
          navigate("/product");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("ProductURL", { value: slug });
  };

  const handleDeleteImage = async (ID: number) => {
    const res = await dispatch(deleteProductImages(ID));
    if (res.meta.requestStatus === "fulfilled") {
      if (ProductID) {
        dispatch(getProductByID(ProductID));
      }
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={ProductDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 40 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ProductID ? "Update Product" : "Create Product"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ProductName"
                      label="Name"
                      placeholder="Name"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      name="ProductURL"
                      label="Product URL"
                      placeholder="Product URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      alternateName="Image"
                      style={{ height: 300 }}
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ProductID"
                      label="Product ID"
                      placeholder="Product ID"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      name={`Finish`}
                      label="Finish"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={FINISHARRAY?.map((finish: any) => {
                        return {
                          value: finish?.label,
                          label: finish?.label,
                        };
                      })}
                    />
                    <Field
                      name="Thickness"
                      label="Thickness"
                      placeholder="Thickness"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      name={`CategoryID`}
                      label="Category"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e) => handleGetSubCategory(e)}
                      options={CategoriesList?.map((category: any) => {
                        return {
                          value: category?.ID,
                          label: category?.CategoryName,
                        };
                      })}
                    />
                    <Field
                      name={`SizeID`}
                      label="Size"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={
                        selectedSubCategory &&
                        selectedCategory &&
                        selectedSubCategory[
                          selectedCategory
                        ]?.SubCategoryList?.map((subCategory: any) => {
                          return {
                            value: subCategory?.ID,
                            label: subCategory?.Name,
                          };
                        })
                      }
                    />
                    <Field
                      name={`SubCategoryID`}
                      label="Sub Category"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={SizeList?.map((size: any) => {
                        return {
                          value: size?.ID,
                          label: size?.Size,
                        };
                      })}
                    />
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    {/* <Field
                      name="Description"
                      label="Description"
                      placeholder="Description"
                      component={FormTextArea}
                      astrike={true}
                      validator={requiredValidator}
                      style={{ height: 130 }}
                    /> */}
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      style={{ height: 380 }}
                      // astrike={true}
                      // validator={requiredValidator}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2} style={{ padding: "20px 0" }}>
                    <Field
                      name={"Images"}
                      label="Upload Images"
                      type={"file"}
                      accept="image/*"
                      multiple={true}
                      component={FormAttachmentUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <>
                      {(!ProductDetail?.ProductImageDetails ||
                        ProductDetail.ProductImageDetails.length === 0) && (
                        <div
                          className="text-center"
                          style={{ color: "#6c757d" }}
                        >
                          <span className="k-icon k-i-image k-icon-xl mb-2">
                            <MdImageNotSupported style={{ fontSize: "24px" }} />
                          </span>
                          <p style={{ color: "#f17272" }} className="mb-0">
                            No images uploaded yet
                          </p>
                        </div>
                      )}
                    </>
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={4}>
                    <div className="card shadow-sm border-0 mb-4">
                      {ProductDetail?.ProductImageDetails?.length > 0 && (
                        <div
                          className="card-body p-3"
                          style={{
                            background: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5
                              className="mb-0"
                              style={{
                                color: "#2c3e50",
                                fontWeight: "600",
                                fontSize: "1.1rem",
                              }}
                            >
                              Uploaded Images
                            </h5>
                            <span className="badge bg-primary rounded-pill">
                              {ProductDetail?.ProductImageDetails?.length || 0}{" "}
                              files
                            </span>
                          </div>

                          <div className="d-flex flex-wrap gap-3">
                            {ProductDetail?.ProductImageDetails?.map(
                              (image: any) => (
                                <div
                                  key={image.ID}
                                  className="position-relative"
                                  style={{
                                    width: "120px",
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                    <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                      <img
                                        className="img-fluid object-fit-cover"
                                        src={image.Image}
                                        alt="Uploaded document"
                                        style={{
                                          cursor: "pointer",
                                          transition: "transform 0.3s",
                                        }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1.05)")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1)")
                                        }
                                        onClick={() =>
                                          window.open(
                                            `${image.Image}`,
                                            "_blank",
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="card-footer p-2 bg-white border-0">
                                      <Button
                                        className="w-100"
                                        size="small"
                                        themeColor="error"
                                        fillMode="outline"
                                        rounded="medium"
                                        style={{
                                          border: "1px solid #dc3545",
                                          fontWeight: "500",
                                        }}
                                        onClick={() =>
                                          handleDeleteImage(image.ID)
                                        }
                                      >
                                        <span className="k-icon k-i-trash me-1"></span>
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} style={{ width: "100%" }}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                    <Field
                      name="MetaKeywords"
                      label="Meta Keywords"
                      component={FormTextField}
                      rows={1}
                    />
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ProductID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/product")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateProduct;
